import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/ui/Toast';
import { ConfirmProvider } from './components/ui/ConfirmDialog';
import { BottomNav } from './components/BottomNav';
import { LoadingScreen } from './components/LoadingScreen';
import { ErrorBoundary } from './components/ErrorBoundary';
import './index.css';

// Public
import { Landing } from './pages/Landing';
import { Auth } from './pages/Auth';
import { PendingApproval } from './pages/PendingApproval';
import { MovedOut } from './pages/MovedOut';
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail').then(m => ({ default: m.VerifyEmail })));
const Privacy = lazy(() => import('./pages/Privacy').then(m => ({ default: m.Privacy })));

// Resident
import { Dashboard } from './pages/Dashboard';
const Providers = lazy(() => import('./pages/Providers').then(m => ({ default: m.Providers })));
const Report = lazy(() => import('./pages/Report').then(m => ({ default: m.Report })));
const News = lazy(() => import('./pages/News').then(m => ({ default: m.News })));
const ArticleDetail = lazy(() => import('./pages/ArticleDetail').then(m => ({ default: m.ArticleDetail })));
const Garden = lazy(() => import('./pages/Garden').then(m => ({ default: m.Garden })));
const ProfilePage = lazy(() => import('./pages/Profile').then(m => ({ default: m.ProfilePage })));
const Polls = lazy(() => import('./pages/Polls').then(m => ({ default: m.Polls })));
const Contacts = lazy(() => import('./pages/Contacts').then(m => ({ default: m.Contacts })));

// Admin
const Admin = lazy(() => import('./pages/Admin').then(m => ({ default: m.Admin })));
const PollManager = lazy(() => import('./pages/PollManager').then(m => ({ default: m.PollManager })));
const NewsAdmin = lazy(() => import('./pages/NewsAdmin').then(m => ({ default: m.NewsAdmin })));
const CalendarAdmin = lazy(() => import('./pages/CalendarAdmin').then(m => ({ default: m.CalendarAdmin })));
const ReportAdmin = lazy(() => import('./pages/ReportAdmin').then(m => ({ default: m.ReportAdmin })));
const ResidentDirectory = lazy(() => import('./pages/ResidentDirectory').then(m => ({ default: m.ResidentDirectory })));
const AlertsAdmin = lazy(() => import('./pages/AlertsAdmin').then(m => ({ default: m.AlertsAdmin })));
const ProvidersAdmin = lazy(() => import('./pages/ProvidersAdmin').then(m => ({ default: m.ProvidersAdmin })));
const AddressesAdmin = lazy(() => import('./pages/AddressesAdmin').then(m => ({ default: m.AddressesAdmin })));
const AddressMap = lazy(() => import('./pages/AddressMap').then(m => ({ default: m.AddressMap })));

/** Where to send a signed-in, approved user who lands on a public page. */
function homeFor(isAdmin: boolean) {
  return isAdmin ? '/admin' : '/dashboard';
}

/** Public pages: if already signed in and approved, go to the app. */
function PublicOnly({ children }: { children: ReactNode }) {
  const { loading, session, isApproved, isAdmin } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (session && isApproved) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? homeFor(isAdmin)} replace />;
  }
  if (session && !isApproved) return <Navigate to="/pending" replace />;
  return <>{children}</>;
}

/** Signed in AND approved. Remembers where the user was going so login can return there. */
function RequireApproved() {
  const { loading, session, isApproved } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (!isApproved) return <Navigate to="/pending" replace />;
  return <Outlet />;
}

/** Admin role. */
function RequireAdmin() {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function PendingGate() {
  const { loading, session, isApproved, isAdmin } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!session) return <Navigate to="/login" replace />;
  if (isApproved) return <Navigate to={homeFor(isAdmin)} replace />;
  // Someone whose address has been taken over keeps their account but not the app.
  if (session.user.account_status === 'moved_out') return <MovedOut />;
  return <PendingApproval />;
}

function OfflineBanner() {
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  if (!offline) return null;
  return <div className="page-offline" role="status">You're offline. Some things won't update until you reconnect.</div>;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0 }); }, [pathname]);
  return null;
}

/** App shell for signed-in residents: bottom nav + page outlet. */
function AppShell() {
  const { isAdmin } = useAuth();
  const location = useLocation();
  return (
    <div className="app">
      {/* Keyed by path so one page's crash doesn't strand the user on the error screen. */}
      <ErrorBoundary key={location.pathname}>
        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      </ErrorBoundary>
      <BottomNav isAdmin={isAdmin} />
      <OfflineBanner />
    </div>
  );
}

function HomeRedirect() {
  const { loading, session, isApproved, isAdmin } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!session) return <Landing />;
  if (!isApproved) return <Navigate to="/pending" replace />;
  return <Navigate to={homeFor(isAdmin)} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<PublicOnly><Auth initialMode="signin" /></PublicOnly>} />
      <Route path="/signup" element={<PublicOnly><Auth initialMode="signup" /></PublicOnly>} />
      <Route path="/forgot-password" element={<Suspense fallback={<LoadingScreen />}><ForgotPassword /></Suspense>} />
      <Route path="/reset-password" element={<Suspense fallback={<LoadingScreen />}><ResetPassword /></Suspense>} />
      <Route path="/verify-email" element={<Suspense fallback={<LoadingScreen />}><VerifyEmail /></Suspense>} />
      <Route path="/privacy" element={<Suspense fallback={<LoadingScreen />}><Privacy /></Suspense>} />
      <Route path="/pending" element={<PendingGate />} />

      <Route element={<RequireApproved />}>
        <Route element={<AppShell />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/providers" element={<Providers />} />
          <Route path="/news" element={<News />} />
          <Route path="/news/:id" element={<ArticleDetail />} />
          <Route path="/report" element={<Report />} />
          <Route path="/report/:id" element={<Report />} />
          <Route path="/garden" element={<Garden />} />
          <Route path="/polls" element={<Polls />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/profile" element={<ProfilePage />} />

          <Route element={<RequireAdmin />}>
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/polls" element={<PollManager />} />
            <Route path="/admin/news" element={<NewsAdmin />} />
            <Route path="/admin/calendar" element={<CalendarAdmin />} />
            <Route path="/admin/reports" element={<ReportAdmin />} />
            <Route path="/admin/directory" element={<ResidentDirectory />} />
            <Route path="/admin/alerts" element={<AlertsAdmin />} />
            <Route path="/admin/providers" element={<ProvidersAdmin />} />
            <Route path="/admin/addresses" element={<AddressesAdmin />} />
            <Route path="/admin/map" element={<AddressMap />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <ConfirmProvider>
              <ScrollToTop />
              <AppRoutes />
            </ConfirmProvider>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
